import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Typography, CircularProgress, Paper } from '@mui/material';
import { toast } from 'react-toastify';
import { getFinancialReport, generateFinancialReport as generateFinancialReportApi } from './api/underwritingApi';
import ReportViewer from './MarkdownReport.js';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

function FinancialReport({ tenantId, effectiveCompanyId }) {
  const [financialReport, setFinancialReport] = useState("");
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const reportRef = useRef(null);

  useEffect(() => {
    const fetchFinancialReport = async () => {
      if (effectiveCompanyId) {
        setIsLoading(true);
        try {
          const result = await getFinancialReport(tenantId, effectiveCompanyId);
          if (result?.data) {
            setFinancialReport(result.data?.report || "");
          }
        } catch (error) {
          console.error("Error fetching financial report:", error);
          setFinancialReport(null); // Set to null on error to distinguish from empty
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    fetchFinancialReport();
  }, [tenantId, effectiveCompanyId]);

  const handleGenerateReport = async () => {
    setIsGeneratingReport(true);
    try {
      const response = await generateFinancialReportApi(tenantId, effectiveCompanyId);
      setFinancialReport(response.report);
      toast.success("Financial report generated successfully!");
    } catch (error) {
      toast.error("Failed to generate financial report.");
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleExport = () => {
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        body * {
          visibility: hidden;
        }
        #financial-report-print-area, #financial-report-print-area * {
          visibility: visible;
        }
        #financial-report-print-area {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
        }
      }
    `;
    document.head.appendChild(style);
    window.print();
    document.head.removeChild(style);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, height: '200px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Financial Report...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', color: '#1A2A56' }}>
          Financial Analysis
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {financialReport && (
            <Button
              variant="outlined"
              onClick={handleExport}
              startIcon={<FileDownloadIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 'bold',
                borderRadius: '8px',
              }}
            >
              Export as PDF
            </Button>
          )}
          {/* <Button
            variant="contained"
            onClick={handleGenerateReport}
            disabled={isGeneratingReport}
            sx={{ 
                textTransform: 'none', 
                fontWeight: 'bold',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                }
            }}
          >
            {isGeneratingReport ? <CircularProgress size={24} color="inherit" /> : "Generate New Report"}
          </Button> */}
        </Box>
      </Box>

      {financialReport ? (
        <Paper ref={reportRef} id="financial-report-print-area" elevation={2} sx={{ p: 3, borderRadius: '8px', backgroundColor: '#fff' }}>
           <ReportViewer markdownText={financialReport} tenantId={tenantId} />
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ textAlign: 'center', p: 4, border: '1px dashed #ccc', borderRadius: '8px', backgroundColor: '#fafafa' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>No Financial Report Available</Typography>
          <Typography variant="body2" color="text.secondary">
            Click "Generate New Report" to create a new financial analysis.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

export default FinancialReport;
